import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Conversation } from "./AIAssistant";
import {
  DollarSign,
  FileText,
  MessageSquare,
  Plus,
  Search,
  X,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ConversationHistoryProps {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  onNewConversation: () => void;
  onSelectConversation: (conversation: Conversation) => void;
  /** Drawer aberto no mobile (no desktop a coluna é fixa). */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const getTypeIcon = (type: Conversation["type"]) => {
  switch (type) {
    case "norma":
      return FileText;
    case "custo":
      return DollarSign;
    case "acao":
      return Zap;
    case "consulta":
    default:
      return MessageSquare;
  }
};

/** Rótulo do grupo de data — mesma lógica de leitura do histórico do RDO. */
const getDateGroup = (date: Date): string => {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const dia = new Date(date);
  dia.setHours(0, 0, 0, 0);

  const diffDias = Math.round((hoje.getTime() - dia.getTime()) / 86_400_000);

  if (diffDias <= 0) return "Hoje";
  if (diffDias === 1) return "Ontem";
  if (diffDias <= 7) return "Últimos 7 dias";
  if (diffDias <= 30) return "Últimos 30 dias";
  return "Mais antigas";
};

export const ConversationHistory = ({
  conversations,
  currentConversation,
  onNewConversation,
  onSelectConversation,
  mobileOpen = false,
  onMobileClose,
}: ConversationHistoryProps) => {
  const [busca, setBusca] = useState("");

  // Agrupa por data preservando a ordem já ordenada (mais recentes primeiro).
  const grupos = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const filtradas = termo
      ? conversations.filter((c) =>
          `${c.title} ${c.lastMessage}`.toLowerCase().includes(termo)
        )
      : conversations;

    const mapa = new Map<string, Conversation[]>();
    filtradas.forEach((conversa) => {
      const rotulo = getDateGroup(conversa.timestamp);
      const atual = mapa.get(rotulo);
      if (atual) atual.push(conversa);
      else mapa.set(rotulo, [conversa]);
    });
    return Array.from(mapa.entries());
  }, [conversations, busca]);

  const temConversas = conversations.length > 0;

  return (
    <>
      {/* Backdrop — só no mobile. z abaixo da sidebar principal (z-[55]/[60]). */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[45] bg-black/50 md:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        style={{ left: mobileOpen ? 0 : "-17rem" }}
        className={cn(
          "flex w-64 shrink-0 flex-col border-r border-border bg-card p-3",
          // Mobile: drawer off-canvas. No desktop md:static torna `left` inerte.
          "fixed inset-y-0 z-[46] w-[17rem]",
          "md:static md:z-auto md:w-64"
        )}
        aria-label="Histórico de conversas"
      >
        <div className="flex items-center justify-between gap-2 md:hidden">
          <span className="text-sm font-semibold">Conversas</span>
          <Button
            variant="ghost"
            size="icon"
            onClick={onMobileClose}
            aria-label="Fechar histórico"
            className="h-11 w-11"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <Button onClick={onNewConversation} className="mt-2 w-full md:mt-0">
          <Plus className="h-4 w-4" />
          Nova conversa
        </Button>

        {temConversas && (
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar conversa"
              aria-label="Buscar conversa"
              className="pl-9"
            />
          </div>
        )}

        <ScrollArea className="-mx-1 mt-3 flex-1">
          <div className="space-y-0.5 px-1">
            {!temConversas ? (
              <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center">
                <MessageSquare className="mx-auto mb-3 h-8 w-8 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">
                  Nenhuma conversa ainda
                </p>
              </div>
            ) : grupos.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                Nada encontrado para “{busca}”.
              </p>
            ) : (
              grupos.map(([rotulo, itens]) => (
                <div key={rotulo}>
                  <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {rotulo}
                  </p>

                  {itens.map((conversa) => {
                    const Icone = getTypeIcon(conversa.type);
                    const ativa = currentConversation?.id === conversa.id;

                    return (
                      <button
                        key={conversa.id}
                        type="button"
                        onClick={() => {
                          onSelectConversation(conversa);
                          onMobileClose?.();
                        }}
                        aria-current={ativa ? "true" : undefined}
                        className={cn(
                          "flex w-full items-start gap-3 rounded-md px-3 py-2 text-left transition-colors",
                          ativa
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:bg-accent hover:text-foreground"
                        )}
                      >
                        <Icone className="mt-0.5 h-4 w-4 shrink-0 opacity-90" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">
                            {conversa.title}
                          </span>
                          {conversa.lastMessage && (
                            <span className="mt-0.5 block truncate text-xs opacity-75">
                              {conversa.lastMessage}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </aside>
    </>
  );
};
